import { useState } from "react";

import {
  AGENTIC_AI_FOLLOW_UP_PLACEHOLDER,
  AGENTIC_AI_INPUT_PLACEHOLDER,
  AGENTIC_AI_SUGGESTED_PROMPTS,
  AGENTIC_AI_TOPICS,
  type SuggestedPrompt
} from "@/components/agenticAI/constants";
import {
  DUMMY_CHAT_HISTORY,
  findDummyConversation
} from "@/components/agenticAI/dummyConversation";

import { STATUS, useLokasiAI } from "@lokasi-ai-web-sdk/app";

/**
 * Container hook for the Agentic AI page. Owns the SDK conversation state and
 * the chat history sidebar so the view components stay props-only.
 *
 * The history list and the conversation it opens are still served by
 * `dummyConversation.ts` — the SDK exposes no endpoint for either yet. Swap
 * `DUMMY_CHAT_HISTORY` / `findDummyConversation` for the real calls once it
 * does; nothing below that swap is dummy-specific.
 */
export const useAgenticAI = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const {
    messages,
    status,
    isThinking,
    error,
    sendPrompt,
    startNewConversation
  } = useLokasiAI();

  const isBusy = isThinking || status === STATUS.IN_PROGRESS;
  const activeConversation = findDummyConversation(activeChatId);
  const hasConversation = Boolean(activeConversation) || messages.length > 0;

  const handleSelectSuggestedPrompt = (suggestion: SuggestedPrompt) => {
    sendPrompt({
      message: suggestion.prompt,
      metadata: { topics: AGENTIC_AI_TOPICS }
    });
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen((isOpen) => !isOpen);
  };

  const handleToggleMapFullScreen = () => {
    setIsMapFullScreen((isExpanded) => !isExpanded);
  };

  // Leaving the conversation leaves the map it belonged to, so the chat column
  // comes back rather than the next chat opening behind a full-screen map.
  const handleNewChat = () => {
    setActiveChatId(null);
    setIsMapFullScreen(false);
    startNewConversation();
  };

  const handleSelectChat = (chatId: string) => {
    setActiveChatId(chatId);
    setIsMapFullScreen(false);
  };

  return {
    activeChatId,
    activeConversation,
    chatHistory: DUMMY_CHAT_HISTORY,
    composerPlaceholder: hasConversation
      ? AGENTIC_AI_FOLLOW_UP_PLACEHOLDER
      : AGENTIC_AI_INPUT_PLACEHOLDER,
    error,
    hasConversation,
    isBusy,
    isMapFullScreen,
    isSidebarOpen,
    suggestedPrompts: AGENTIC_AI_SUGGESTED_PROMPTS,
    handleNewChat,
    handleSelectChat,
    handleSelectSuggestedPrompt,
    handleToggleMapFullScreen,
    handleToggleSidebar
  };
};
